import { Component, Input, Output, EventEmitter, OnInit, forwardRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, FormArray, Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors
 } from '@angular/forms';
import { debounceTime, filter } from 'rxjs/operators';
import { validationmethod } from 'src/app/shared/models/validationmethod';




@Component({
    selector: 'app-expression-builder',
    templateUrl: './expression-builder.component.html',
    styleUrls: ['./expression-builder.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpressionBuilderComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ExpressionBuilderComponent),
            multi: true
        }
    ],
    standalone: false
})

export class ExpressionBuilderComponent  implements OnInit, ControlValueAccessor, Validator {

  private onChange: any = () => {};
  private onTouched: any = () => {};
  private onValidatorChange = () => {};

  @Input() level = 0;
  @Input() form!: FormGroup;
  @Input() isRoot = false;


  @Input() fields: { metric_id: number; fieldName: string }[] = [];

  @Input() operators: { label: string; value: string }[] = [];
  @Input() groupOperators: string[] = ['AND', 'OR'];


  @Output() expressionChange = new EventEmitter<any>();

  

  constructor(private fb: FormBuilder,
              private snackBar: MatSnackBar,
              private validateMethods: validationmethod
  ) {}

  ngOnInit(): void {
      if (!this.form) {
    this.form = this.createGroup(); // root only
  }

    // if (this.initialValue) {
    //   this.patchForm(this.form, this.initialValue);
    // }

    // this.form.valueChanges.subscribe(val => {
    //   this.expressionChange.emit(val);
    // });
    // Listen to overall form changes
    this.form.valueChanges
      .pipe(
        debounceTime(300) // Debounce for performance
      )
      .subscribe(val => {
        this.onChange(val);
        // this.onTouched();
        this.expressionChange.emit(val);
      });
  }

  
  /** Called by Angular when parent sets value */
  writeValue(value: any): void {
    if (value) {
      this.patchForm(this.form, value);
    } else {
      this.form.reset({ condition: 'AND', rules: [] });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  registerOnValidatorChange(fn: () => void): void {
  this.onValidatorChange = fn;
}

  /** Create a new group */
  createGroup(): FormGroup {
    return this.fb.group({
      condition: ['AND'],
      rules: this.fb.array([])
    });
  }

  /** Getter for rules array */
  getRulesArray(formGroup: FormGroup): FormArray {
    return formGroup.get('rules') as FormArray;
  }

  /** Add a simple rule to a group */
  addRule(formGroup: FormGroup) {
    this.getRulesArray(formGroup).push(
      this.fb.group({
        metric_id: ['', Validators.required],
        fieldName: [''],
        operator: ['', Validators.required],
        value: ['', [Validators.required,this.validateMethods.restrictAllSpecialCharactersExceptHyphenAndUnderscore]]
      })
    );
    this.emitChange();
  }

  /** Add a nested group */
  addGroup(formGroup: FormGroup) {
  if (!this.hasAtLeastOneValidRule(formGroup)) {
    this.snackBar.open(" At least one valid rule is required in this group","Ok", { duration: 3000 })
    return;
  }
    this.getRulesArray(formGroup).push(this.createGroup());
    this.emitChange();

  }

  /** Remove rule/group */
  removeRule(formGroup: FormGroup, index: number) {
    this.getRulesArray(formGroup).removeAt(index);
    this.emitChange();

  }

  /** Check if a form group is a group (has rules) */
  isGroup(rule: any): boolean {
    return rule.get('rules') !== null;
  }

  /** Recursive patching for edit mode */
  patchForm(form: FormGroup, value: any) {
    form.get('condition')?.setValue(value.condition || 'AND');
    const rulesArray = this.getRulesArray(form);
    rulesArray.clear();

    if (value.rules && value.rules.length) {
      value.rules.forEach((rule: any) => {
        if (rule.rules) {
          const group = this.createGroup();
          this.patchForm(group, rule);
          rulesArray.push(group);
        } else {
          rulesArray.push(this.fb.group({
            metric_id: [rule.metric_id, Validators.required],
            fieldName: [rule.fieldName],
            operator: [rule.operator, Validators.required],
            value: [rule.value, [Validators.required,this.validateMethods.restrictAllSpecialCharactersExceptHyphenAndUnderscore]]
          }));
        }
      });
    }
  }

onChildExpressionChange(index: number, value: any) {
  const rulesArray = this.getRulesArray(this.form);
  const rebuiltGroup = this.createGroup();
  this.patchForm(rebuiltGroup, value);

  rulesArray.setControl(index, rebuiltGroup);
  this.emitChange(); // 🔥 required
}



  /** Convert JSON to human readable text recursively */
  toReadableText(node: any): string {
    if (node.rules) {
      return '(' + node.rules
        .map(r => this.toReadableText(r))
        .join(` ${node.condition} `) + ')';
    }

     const operatorLabel = this.getOperatorLabel(node.operator);
     
    return `${node.fieldName} ${operatorLabel} ${node.value}`;
  }

  get readableLogic(): string {
    return this.toReadableText(this.form.value);
  }

  isRuleInvalid(rule: FormGroup): boolean {
  return rule.invalid && (rule.dirty || rule.touched);
}

hasAtLeastOneValidRule(formGroup: FormGroup): boolean {
  const rules = this.getRulesArray(formGroup);
  return rules.controls.some(ctrl =>
    !this.isGroup(ctrl) && ctrl.valid
  );
}

isGroupValid(group: FormGroup): boolean {
  const rules = this.getRulesArray(group);
  if (!rules || rules.length === 0) return false;

  return rules.controls.some(ctrl =>
    this.isGroup(ctrl)
      ? this.isGroupValid(ctrl as FormGroup)
      : ctrl.valid
  );
}


validate(control: AbstractControl): ValidationErrors | null {
  console.log("Validating expression builder", control.value);
  // if (!control.touched && !control.dirty) {
  //   return null; //  no validation initially
  // }

  //   if (control.touched) {
  //   this.markAllControlsTouched(this.form);
  // }

  return this.isGroupValid(this.form)
    ? null
    : { invalidExpression: true };
}



 private emitChange() {
    if (!this.isRoot) return;

    const value = this.form.getRawValue();
    
    // Emit everything, even if invalid
    this.onChange(value);
    this.expressionChange.emit(value);
    
    this.form.updateValueAndValidity({ emitEvent: false });
    this.onValidatorChange();
  }

  onFieldChange(fieldId: number, rule: FormGroup) {
  const selected = this.fields.find(f => f.metric_id === fieldId);
  rule.patchValue({
    fieldName: selected?.fieldName || ''
  });
}

getOperatorLabel(value: string): string {
  return this.operators.find(op => op.value === value)?.label || value;
}

markAllControlsTouched(formGroup: FormGroup | FormArray) {
  console.log("Marking all controls as touched");
  Object.values(formGroup.controls).forEach(control => {
    if (control instanceof FormGroup || control instanceof FormArray) {
      this.markAllControlsTouched(control);
    } else {
      control.markAsTouched();
    }
  });
}
public markAllAsTouchedDeep() {
  this.markAllControlsTouched(this.form);
}


}
